import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import { adminApi } from '../../services/adminApi';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './AdminPanel.css';

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalRooms: number;
  activeRooms: number;
  totalGames: number;
  gamesInProgress: number;
}

interface User {
  _id: string;
  username: string;
  email: string;
  displayName: string;
  gamesPlayed: number;
  gamesWon: number;
  level: number;
  winRate: number;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  role?: string;
}

interface GameRoom {
  _id: string;
  roomId: string;
  roomName?: string;
  gameType: string;
  status: string;
  maxPlayers: number;
  createdBy: string;
  createdByUsername?: string;
  createdAt: string;
  lastActivityAt: string;
  totalGamesPlayed: number;
}

const AdminPanelNew: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showSuccess } = useNotifications();

  // State
  const [currentTab, setCurrentTab] = useState<'dashboard' | 'users' | 'rooms' | 'system'>('dashboard');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [rooms, setRooms] = useState<GameRoom[]>([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [, setRefreshing] = useState(false);
  
  // Dashboard enhanced stats
  const [, setDashboardStats] = useState<{
    users: { total: number; active: number; recentActive: number; admins: number; superAdmins: number };
    games: { total: number; active: number; recent: number };
    timestamp: Date;
  } | null>(null);
  const [health, setHealth] = useState<any>(null);
  const isSuperAdmin = user?.isSuperAdmin === true;
  
  // User filters and pagination
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userPageSize, setUserPageSize] = useState(20);
  const [autoPageSize, setAutoPageSize] = useState(false);
  
  // Create user dialog
  const [createUserDialog, setCreateUserDialog] = useState({
    isOpen: false,
    username: '',
    email: '',
    password: '',
    displayName: '',
    role: 'user',
    isLoading: false
  });

  // Announcement dialog
  const [announcementDialog, setAnnouncementDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    isLoading: false
  });

  // System settings
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [systemLogs, setSystemLogs] = useState<any[]>([]);
  
  // Room filters and pagination
  const [roomSearch, setRoomSearch] = useState('');
  const [roomStatusFilter, setRoomStatusFilter] = useState('');
  const [roomGameTypeFilter, setRoomGameTypeFilter] = useState('');
  const [roomPage, setRoomPage] = useState(1);
  const [roomTotalPages, setRoomTotalPages] = useState(1);
  const [roomPageSize, setRoomPageSize] = useState(20);
  
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info';
    onConfirm: () => void | Promise<void>;
    isLoading?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    isLoading: false
  });

  // Check if user is admin or superadmin using role field
  const isAdmin = user?.role === 'admin' || user?.isSuperAdmin === true;

  // Auto calculate page size based on viewport height
  useEffect(() => {
    if (!autoPageSize) return;
    
    const calculatePageSize = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = 200; // Approx header + filters height
      const rowHeight = 60; // Approx table row height
      const paginationHeight = 60;
      const availableHeight = viewportHeight - headerHeight - paginationHeight;
      const calculatedSize = Math.floor(availableHeight / rowHeight);
      const optimalSize = Math.max(5, Math.min(calculatedSize, 50));
      
      if (optimalSize !== userPageSize) {
        setUserPageSize(optimalSize);
        setUserPage(1);
      }
    };
    
    calculatePageSize();
    window.addEventListener('resize', calculatePageSize);
    return () => window.removeEventListener('resize', calculatePageSize);
  }, [autoPageSize]);

  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
      loadSystemStats();
      loadUsers();
      loadRooms();
      loadSystemSettings();
      loadSystemLogs();
    }
  }, [isAdmin]);
  
  // Auto-refresh dashboard every 30 seconds
  useEffect(() => {
    if (!isAdmin || currentTab !== 'dashboard') return;
    
    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isAdmin, currentTab]);

  // Reload users when filters or pagination changes
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [userPage, userPageSize, userSearch, userRoleFilter, userStatusFilter]);

  // Reload rooms when filters or pagination changes
  useEffect(() => {
    if (isAdmin) {
      loadRooms();
    }
  }, [roomPage, roomPageSize, roomSearch, roomStatusFilter, roomGameTypeFilter]);

  const loadSystemStats = async () => {
    setLoading(true);
    try {
      // Use real API to get platform stats
      const stats = await adminApi.getPlatformStats();
      
      setSystemStats({
        totalUsers: stats.users.total,
        activeUsers: stats.users.active,
        totalRooms: 0, // Not available in current API
        activeRooms: 0, // Not available in current API
        totalGames: stats.games.total,
        gamesInProgress: stats.games.active
      });

    } catch (error) {
      console.error('Error loading system stats:', error);
      showError('Load Failed', 'Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDashboardData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const statsData = await adminApi.getPlatformStats();
      setDashboardStats({
        users: {
          total: statsData.users.total,
          active: statsData.users.active,
          recentActive: statsData.users.recentActive,
          admins: statsData.users.admins,
          superAdmins: statsData.users.superAdmins
        },
        games: {
          total: statsData.games.total,
          active: statsData.games.active,
          recent: statsData.games.recent
        },
        timestamp: new Date()
      });

      if (isSuperAdmin) {
        const healthData = await adminApi.getSystemHealth();
        setHealth(healthData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (!silent) {
        showError('Load Failed', 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response: any = await adminApi.getUsers({ 
        page: userPage, 
        limit: userPageSize,
        search: userSearch || undefined,
        role: userRoleFilter || undefined,
        isActive: userStatusFilter === 'active' ? true : userStatusFilter === 'inactive' ? false : undefined
      });
      if (response.success && response.data) {
        setUsers(response.data.users);
        setUserTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError('Load Failed', 'Failed to load user list');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadRooms = async () => {
    setRoomsLoading(true);
    try {
      const response: any = await adminApi.getRooms({ 
        page: roomPage, 
        limit: roomPageSize,
        search: roomSearch || undefined,
        status: roomStatusFilter || undefined,
        gameType: roomGameTypeFilter || undefined
      });
      if (response.success && response.data) {
        setRooms(response.data.rooms);
        setRoomTotalPages(response.data.pagination.totalPages);
        
        // Update system stats with room counts
        if (systemStats) {
          const activeRooms = response.data.rooms.filter((r: GameRoom) => r.status === 'active').length;
          setSystemStats({
            ...systemStats,
            totalRooms: response.data.pagination.total,
            activeRooms
          });
        }
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      showError('Load Failed', 'Failed to load room list');
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    const { username, email, password, displayName, role } = createUserDialog;
    
    if (!username || !email || !password) {
      showError('Validation Error', 'Username, email, and password are required');
      return;
    }
    
    setCreateUserDialog(prev => ({ ...prev, isLoading: true }));
    
    try {
      const response: any = await adminApi.createUser({
        username,
        email,
        password,
        displayName: displayName || username,
        role
      });
      
      if (response.success) {
        showSuccess('User Created', `${username} has been created successfully`);
        setCreateUserDialog({
          isOpen: false,
          username: '',
          email: '',
          password: '',
          displayName: '',
          role: 'user',
          isLoading: false
        });
        loadUsers(); // Reload users list
      }
    } catch (error: any) {
      setCreateUserDialog(prev => ({ ...prev, isLoading: false }));
      showError('Creation Failed', error.message || 'Failed to create user');
    }
  };

  const handleSendAnnouncement = async () => {
    const { title, message, type } = announcementDialog;
    
    if (!title || !message) {
      showError('Validation Error', 'Title and message are required');
      return;
    }
    
    setAnnouncementDialog(prev => ({ ...prev, isLoading: true }));
    
    try {
      const response: any = await adminApi.sendAnnouncement({ title, message, type });
      
      if (response.success) {
        showSuccess('Announcement Sent', 'Announcement has been sent to all users');
        setAnnouncementDialog({
          isOpen: false,
          title: '',
          message: '',
          type: 'info',
          isLoading: false
        });
      }
    } catch (error: any) {
      setAnnouncementDialog(prev => ({ ...prev, isLoading: false }));
      showError('Send Failed', error.message || 'Failed to send announcement');
    }
  };

  const handleClearCache = async () => {
    try {
      const response: any = await adminApi.clearCache();
      if (response.success) {
        showSuccess('Cache Cleared', 'Server cache has been cleared successfully');
      }
    } catch (error: any) {
      showError('Clear Failed', error.message || 'Failed to clear cache');
    }
  };

  const handleEmergencyStop = async () => {
    try {
      const response: any = await adminApi.emergencyStop();
      if (response.success) {
        showSuccess('Emergency Stop Complete', `${response.data.roomsClosed} rooms have been closed`);
        loadRooms(); // Reload rooms to show updated status
      }
    } catch (error: any) {
      showError('Emergency Stop Failed', error.message || 'Failed to execute emergency stop');
    }
  };

  const loadSystemSettings = async () => {
    try {
      const response: any = await adminApi.getSettings();
      if (response.success) {
        setSystemSettings(response.data);
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
    }
  };

  const loadSystemLogs = async () => {
    try {
      const response: any = await adminApi.getSystemLogs(50);
      if (response.success && response.data.logs) {
        setSystemLogs(response.data.logs);
      }
    } catch (error: any) {
      console.error('Error loading logs:', error);
    }
  };

  const handleSettingToggle = async (setting: string, value: boolean) => {
    try {
      const response: any = await adminApi.updateSettings({ [setting]: value });
      if (response.success) {
        setSystemSettings((prev: any) => ({ ...prev, [setting]: value }));
        showSuccess('Setting Updated', `${setting} has been ${value ? 'enabled' : 'disabled'}`);
      }
    } catch (error: any) {
      showError('Update Failed', error.message || 'Failed to update setting');
    }
  };

  const handleUserAction = async (userId: string, action: 'activate' | 'deactivate' | 'delete') => {
    const targetUser = users.find(u => u._id === userId);
    if (!targetUser) return;

    const messages = {
      activate: {
        title: 'Activate User',
        message: `Are you sure you want to activate ${targetUser.username}?`,
        type: 'info' as const
      },
      deactivate: {
        title: 'Deactivate User',
        message: `Are you sure you want to deactivate ${targetUser.username}? They will not be able to log in.`,
        type: 'warning' as const
      },
      delete: {
        title: 'Delete User',
        message: `WARNING: Are you sure you want to permanently delete ${targetUser.username}? This action cannot be undone. All their data will be lost.`,
        type: 'danger' as const
      }
    };

    setConfirmDialog({
      ...messages[action],
      isOpen: true,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        
        try {
          if (action === 'delete') {
            const response: any = await adminApi.deleteUser(userId);
            if (response.success) {
              showSuccess('User Deleted', `${targetUser.username} has been deleted`);
              setUsers(prev => prev.filter(u => u._id !== userId));
            }
          } else {
            // Toggle active status
            const response: any = await adminApi.toggleUserActive(userId);
            if (response.success) {
              showSuccess(
                action === 'activate' ? 'User Activated' : 'User Deactivated',
                `${targetUser.username} has been ${action}d`
              );
              setUsers(prev => prev.map(u => 
                u._id === userId ? { ...u, isActive: action === 'activate' } : u
              ));
            }
          }
          
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          showError('Action Failed', error.message || `Failed to ${action} user`);
        }
      }
    });
  };

  const handleRoomAction = async (roomId: string, action: 'close' | 'delete') => {
    const room = rooms.find(r => r._id === roomId);
    if (!room) return;

    const roomName = room.roomName || room.roomId;

    const messages = {
      close: {
        title: 'Close Room',
        message: `Are you sure you want to close "${roomName}"? The room will be marked as abandoned.`,
        type: 'warning' as const
      },
      delete: {
        title: 'Delete Room',
        message: `WARNING: Are you sure you want to permanently delete "${roomName}"? This action cannot be undone. All room data will be lost.`,
        type: 'danger' as const
      }
    };

    setConfirmDialog({
      ...messages[action],
      isOpen: true,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        
        try {
          if (action === 'delete') {
            const response: any = await adminApi.deleteRoom(roomId);
            if (response.success) {
              showSuccess('Room Deleted', `${roomName} has been permanently deleted`);
              setRooms(prev => prev.filter(r => r._id !== roomId));
            }
          } else if (action === 'close') {
            const response: any = await adminApi.closeRoom(roomId);
            if (response.success) {
              showSuccess('Room Closed', `${roomName} has been closed`);
              setRooms(prev => prev.map(r => 
                r._id === roomId ? { ...r, status: 'abandoned' } : r
              ));
            }
          }
          
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          showError('Action Failed', error.message || `Failed to ${action} room`);
        }
      }
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-300">You do not have administrator privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-container">
      {/* Header */}
      <div className="admin-header">
        <div className="flex justify-between items-center">
          <h1 className="admin-header-title">🛠️ Admin Panel</h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-300">Welcome, {user?.displayName || user?.username}</span>
            <button 
              onClick={() => navigate('/dashboard')}
              className="admin-button secondary"
            >
              ← Back to Game
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-nav-tabs">
        <div className="flex space-x-1 p-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'rooms', label: 'Rooms', icon: '🎯' },
            { id: 'system', label: 'System', icon: '⚙️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id as any)}
              className={`admin-tab ${currentTab === tab.id ? 'active' : ''}`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Dashboard Tab */}
        {currentTab === 'dashboard' && (
          <div className="space-y-6">
            <h2 className="admin-section-title">System Overview</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner"></div>
              </div>
            ) : systemStats ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="stat-card blue">
                    <div className="stat-card-icon">👥</div>
                    <div className="stat-card-value">{systemStats.totalUsers}</div>
                    <div className="stat-card-label">Total Users</div>
                  </div>
                  <div className="stat-card green">
                    <div className="stat-card-icon">✓</div>
                    <div className="stat-card-value">{systemStats.activeUsers}</div>
                    <div className="stat-card-label">Active Users</div>
                  </div>
                  <div className="stat-card purple">
                    <div className="stat-card-icon">🏠</div>
                    <div className="stat-card-value">{systemStats.totalRooms}</div>
                    <div className="stat-card-label">Total Rooms</div>
                  </div>
                  <div className="stat-card yellow">
                    <div className="stat-card-icon">▶</div>
                    <div className="stat-card-value">{systemStats.activeRooms}</div>
                    <div className="stat-card-label">Active Rooms</div>
                  </div>
                  <div className="stat-card indigo">
                    <div className="stat-card-icon">🎮</div>
                    <div className="stat-card-value">{systemStats.totalGames}</div>
                    <div className="stat-card-label">Total Games</div>
                  </div>
                  <div className="stat-card red">
                    <div className="stat-card-icon">🎯</div>
                    <div className="stat-card-value">{systemStats.gamesInProgress}</div>
                    <div className="stat-card-label">Games In Progress</div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button 
                      onClick={loadSystemStats}
                      className="action-card blue"
                    >
                      <div className="action-card-icon">🔄</div>
                      <div className="action-card-title">Refresh Stats</div>
                    </button>
                    <button 
                      onClick={() => setAnnouncementDialog({ ...announcementDialog, isOpen: true })}
                      className="action-card green"
                    >
                      <div className="action-card-icon">📢</div>
                      <div className="action-card-title">Send Announcement</div>
                    </button>
                    <button 
                      onClick={() => {
                        setConfirmDialog({
                          isOpen: true,
                          title: 'Clear Cache',
                          message: 'Are you sure you want to clear the server cache? This may temporarily affect performance.',
                          type: 'warning',
                          onConfirm: async () => {
                            await handleClearCache();
                            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                          }
                        });
                      }}
                      className="action-card yellow"
                    >
                      <div className="action-card-icon">🧹</div>
                      <div className="action-card-title">Clear Cache</div>
                    </button>
                    <button 
                      onClick={() => {
                        setConfirmDialog({
                          isOpen: true,
                          title: 'Emergency Stop',
                          message: 'WARNING: This will stop all active games and disconnect all players. This action should only be used in emergencies. Are you absolutely sure?',
                          type: 'danger',
                          onConfirm: async () => {
                            await handleEmergencyStop();
                            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                          }
                        });
                      }}
                      className="action-card red"
                    >
                      <div className="action-card-icon">🚨</div>
                      <div className="action-card-title">Emergency Stop</div>
                    </button>
                  </div>
                </div>

                {/* System Health - Super Admin Only */}
                {isSuperAdmin && health && (
                  <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-gray-700/50 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold flex items-center">
                        <span className="mr-2">💊</span>
                        System Health
                        <span className={`ml-3 px-3 py-1 rounded-full text-xs font-semibold ${
                          health.status === 'healthy' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          health.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                          'bg-red-500/20 text-red-400 border border-red-500/30'
                        }`}>
                          {health.status.toUpperCase()}
                        </span>
                      </h3>
                      <div className="text-sm text-gray-400">
                        Uptime: {Math.floor(health.uptime / 3600)}h {Math.floor((health.uptime % 3600) / 60)}m
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Database Health */}
                      <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400 text-sm">Database</span>
                          <span className={`w-3 h-3 rounded-full ${health.database.connected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></span>
                        </div>
                        <div className="text-white font-semibold">
                          {health.database.connected ? 'Connected' : 'Disconnected'}
                        </div>
                        {health.database.responseTime && (
                          <div className="text-xs text-gray-500 mt-1">
                            {health.database.responseTime}ms
                          </div>
                        )}
                      </div>

                      {/* Redis Health */}
                      {health.redis && (
                        <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-400 text-sm">Redis</span>
                            <span className={`w-3 h-3 rounded-full ${health.redis.connected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></span>
                          </div>
                          <div className="text-white font-semibold">
                            {health.redis.connected ? 'Connected' : 'Disconnected'}
                          </div>
                          {health.redis.responseTime && (
                            <div className="text-xs text-gray-500 mt-1">
                              {health.redis.responseTime}ms
                            </div>
                          )}
                        </div>
                      )}

                      {/* Memory Usage */}
                      <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400 text-sm">Memory</span>
                          <span className={`text-xs font-semibold ${
                            health.memory.percentage < 70 ? 'text-green-400' :
                            health.memory.percentage < 85 ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {health.memory.percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-white font-semibold">
                          {(health.memory.used / 1024 / 1024).toFixed(0)} MB
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          of {(health.memory.total / 1024 / 1024).toFixed(0)} MB
                        </div>
                      </div>

                      {/* Environment */}
                      <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400 text-sm">Environment</span>
                        </div>
                        <div className="text-white font-semibold capitalize">
                          {health.environment}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Node.js
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Analytics & Trends */}
                {systemStats && (
                  <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-gray-700/50 shadow-xl">
                    <h3 className="text-lg font-semibold mb-6 flex items-center bg-gradient-to-r from-indigo-400 to-purple-600 bg-clip-text text-transparent">
                      <span className="mr-2 text-2xl">📊</span>
                      Platform Analytics
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* User Activity Chart */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">User Engagement</h4>
                        
                        {/* Active Users Progress */}
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-2">
                            <span>Active Users</span>
                            <span>{systemStats.activeUsers} / {systemStats.totalUsers}</span>
                          </div>
                          <div className="h-3 bg-gray-900/50 rounded-full overflow-hidden border border-gray-700/50">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                              style={{ width: `${(systemStats.activeUsers / systemStats.totalUsers * 100).toFixed(0)}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {((systemStats.activeUsers / systemStats.totalUsers) * 100).toFixed(1)}% active
                          </div>
                        </div>

                        {/* Games in Progress */}
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-2">
                            <span>Games Running</span>
                            <span>{systemStats.gamesInProgress} / {systemStats.totalGames}</span>
                          </div>
                          <div className="h-3 bg-gray-900/50 rounded-full overflow-hidden border border-gray-700/50">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500"
                              style={{ width: `${systemStats.totalGames > 0 ? (systemStats.gamesInProgress / systemStats.totalGames * 100).toFixed(0) : 0}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {systemStats.totalGames > 0 ? ((systemStats.gamesInProgress / systemStats.totalGames) * 100).toFixed(1) : 0}% in progress
                          </div>
                        </div>

                        {/* Active Rooms */}
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-2">
                            <span>Active Rooms</span>
                            <span>{systemStats.activeRooms} / {systemStats.totalRooms}</span>
                          </div>
                          <div className="h-3 bg-gray-900/50 rounded-full overflow-hidden border border-gray-700/50">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-400 transition-all duration-500"
                              style={{ width: `${systemStats.totalRooms > 0 ? (systemStats.activeRooms / systemStats.totalRooms * 100).toFixed(0) : 0}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {systemStats.totalRooms > 0 ? ((systemStats.activeRooms / systemStats.totalRooms) * 100).toFixed(1) : 0}% active
                          </div>
                        </div>
                      </div>

                      {/* Platform Stats Visual */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">Platform Overview</h4>
                        
                        {/* Total Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 p-4 rounded-lg border border-blue-700/30">
                            <div className="text-3xl font-bold text-blue-400">{systemStats.totalUsers}</div>
                            <div className="text-xs text-blue-300 mt-1">Total Users</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {systemStats.activeUsers} active
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 p-4 rounded-lg border border-green-700/30">
                            <div className="text-3xl font-bold text-green-400">{systemStats.totalGames}</div>
                            <div className="text-xs text-green-300 mt-1">Total Games</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {systemStats.gamesInProgress} playing
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 p-4 rounded-lg border border-purple-700/30">
                            <div className="text-3xl font-bold text-purple-400">{systemStats.totalRooms}</div>
                            <div className="text-xs text-purple-300 mt-1">Total Rooms</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {systemStats.activeRooms} active
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 p-4 rounded-lg border border-yellow-700/30">
                            <div className="text-3xl font-bold text-yellow-400">
                              {systemStats.totalGames > 0 ? ((systemStats.gamesInProgress / systemStats.totalGames) * 100).toFixed(0) : 0}%
                            </div>
                            <div className="text-xs text-yellow-300 mt-1">Game Activity</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Live engagement
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {/* Users Tab */}
        {currentTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="admin-section-title">User Management</h2>
              <button 
                onClick={() => setCreateUserDialog({ ...createUserDialog, isOpen: true })}
                className="admin-button success"
              >
                + Add User
              </button>
            </div>

            {/* Search and Filters */}
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Search Users
                  </label>
                  <input
                    type="text"
                    placeholder="Username, email, or display name..."
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setUserPage(1); // Reset to page 1 on search
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={userRoleFilter}
                    onChange={(e) => {
                      setUserRoleFilter(e.target.value);
                      setUserPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Roles</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={userStatusFilter}
                    onChange={(e) => {
                      setUserStatusFilter(e.target.value);
                      setUserPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Page Size and Clear Filters */}
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <label className="text-sm text-gray-300">Show:</label>
                  <select
                    value={userPageSize}
                    onChange={(e) => {
                      setUserPageSize(parseInt(e.target.value));
                      setUserPage(1);
                      setAutoPageSize(false);
                    }}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={autoPageSize}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="text-sm text-gray-300">per page</span>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoPageSize}
                      onChange={(e) => setAutoPageSize(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Auto-fit</span>
                  </label>
                </div>

                {(userSearch || userRoleFilter || userStatusFilter) && (
                  <button
                    onClick={() => {
                      setUserSearch('');
                      setUserRoleFilter('');
                      setUserStatusFilter('');
                      setUserPage(1);
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="info-banner">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-blue-200">No Users Found</h4>
                    <p className="text-sm text-blue-300 mt-1">
                      No users are currently registered in the system.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-container">
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Level</th>
                        <th>Games</th>
                        <th>Win Rate</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user._id}>
                          <td>
                            <div>
                              <div className="font-medium text-white">{user.username}</div>
                              <div className="text-sm text-gray-400">{user.displayName}</div>
                            </div>
                          </td>
                          <td className="text-sm text-gray-300">{user.email}</td>
                          <td>
                            <span className="badge blue">
                              Level {user.level}
                            </span>
                          </td>
                          <td className="text-sm text-gray-300">{user.gamesPlayed}</td>
                          <td className="text-sm text-gray-300">
                            {user.winRate}%
                          </td>
                          <td>
                            <span className={`badge ${user.isActive ? 'success' : 'danger'}`}>
                              {user.isActive ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </td>
                          <td className="text-sm text-gray-400">
                            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td>
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleUserAction(user._id, user.isActive ? 'deactivate' : 'activate')}
                                className={`admin-button ${user.isActive ? 'warning' : 'success'}`}
                              >
                                {user.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <button 
                                onClick={() => handleUserAction(user._id, 'delete')}
                                className="admin-button danger"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {userTotalPages > 1 && (
                  <div className="flex justify-between items-center mt-4 px-4 py-3 bg-gray-800/50 rounded-lg">
                    <div className="text-sm text-gray-400">
                      Page {userPage} of {userTotalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setUserPage(1)}
                        disabled={userPage === 1}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setUserPage(userPage - 1)}
                        disabled={userPage === 1}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setUserPage(userPage + 1)}
                        disabled={userPage === userTotalPages}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setUserPage(userTotalPages)}
                        disabled={userPage === userTotalPages}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rooms Tab */}
        {currentTab === 'rooms' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="admin-section-title">🎯 Room Management</h2>
              <button 
                onClick={loadRooms}
                className="admin-button secondary"
              >
                🔄 Refresh
              </button>
            </div>

            {/* Search and Filters - Glassmorphism Style */}
            <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-gray-700/50 shadow-xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Search Rooms
                  </label>
                  <input
                    type="text"
                    placeholder="Room name or ID..."
                    value={roomSearch}
                    onChange={(e) => {
                      setRoomSearch(e.target.value);
                      setRoomPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={roomStatusFilter}
                    onChange={(e) => {
                      setRoomStatusFilter(e.target.value);
                      setRoomPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </div>

                {/* Game Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Game Type
                  </label>
                  <select
                    value={roomGameTypeFilter}
                    onChange={(e) => {
                      setRoomGameTypeFilter(e.target.value);
                      setRoomPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Types</option>
                    <option value="kadi-ni-tidi">Kadi ni Tidi</option>
                    <option value="teen-patti">Teen Patti</option>
                    <option value="rummy">Rummy</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Page Size and Clear Filters */}
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <label className="text-sm text-gray-300">Show:</label>
                  <select
                    value={roomPageSize}
                    onChange={(e) => {
                      setRoomPageSize(parseInt(e.target.value));
                      setRoomPage(1);
                    }}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="text-sm text-gray-300">per page</span>
                </div>

                {(roomSearch || roomStatusFilter || roomGameTypeFilter) && (
                  <button
                    onClick={() => {
                      setRoomSearch('');
                      setRoomStatusFilter('');
                      setRoomGameTypeFilter('');
                      setRoomPage(1);
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {roomsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loading-spinner"></div>
              </div>
            ) : rooms.length === 0 ? (
              <div className="info-banner">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-blue-200">No Rooms Found</h4>
                    <p className="text-sm text-blue-300 mt-1">
                      No game rooms are currently in the system.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="admin-table-container">
                <div className="overflow-x-auto">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Room Name</th>
                        <th>Game Type</th>
                        <th>Max Players</th>
                        <th>Status</th>
                        <th>Games Played</th>
                        <th>Created By</th>
                        <th>Last Activity</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rooms.map((room) => (
                        <tr key={room._id}>
                          <td className="font-medium text-white">{room.roomName || room.roomId}</td>
                          <td className="text-sm text-gray-300 capitalize">{room.gameType}</td>
                          <td className="text-sm text-gray-300">{room.maxPlayers}</td>
                          <td>
                            <span className={`badge ${
                              room.status === 'active' ? 'success' : 
                              room.status === 'completed' ? 'blue' : 'warning'
                            }`}>
                              {room.status === 'active' ? '🟢 Active' : 
                               room.status === 'completed' ? '✓ Completed' : 
                               '⏸ Abandoned'}
                            </span>
                          </td>
                          <td className="text-sm text-gray-300">{room.totalGamesPlayed}</td>
                          <td className="text-sm text-gray-300">{room.createdByUsername || 'Unknown'}</td>
                          <td className="text-sm text-gray-400">
                            {new Date(room.lastActivityAt).toLocaleDateString()}
                          </td>
                          <td>
                            <div className="flex space-x-2">
                              {room.status === 'active' && (
                                <button 
                                  onClick={() => handleRoomAction(room._id, 'close')}
                                  className="admin-button warning"
                                >
                                  Close
                                </button>
                              )}
                              {(room.status === 'completed' || room.status === 'abandoned') && (
                                <button 
                                  onClick={() => handleRoomAction(room._id, 'delete')}
                                  className="admin-button danger"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {roomTotalPages > 1 && (
                  <div className="flex justify-between items-center mt-4 px-4 py-3 bg-gray-800/50 rounded-lg">
                    <div className="text-sm text-gray-400">
                      Page {roomPage} of {roomTotalPages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setRoomPage(1)}
                        disabled={roomPage === 1}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setRoomPage(roomPage - 1)}
                        disabled={roomPage === 1}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setRoomPage(roomPage + 1)}
                        disabled={roomPage === roomTotalPages}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setRoomPage(roomTotalPages)}
                        disabled={roomPage === roomTotalPages}
                        className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* System Tab */}
        {currentTab === 'system' && (
          <div className="space-y-6">
            <h2 className="admin-section-title">⚙️ System Configuration</h2>

            {/* Info Banner - Glassmorphism */}
            <div className="bg-yellow-900/30 backdrop-filter backdrop-blur-lg border border-yellow-700/50 p-5 rounded-xl shadow-xl">
              <div className="flex items-start space-x-3">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h4 className="font-semibold text-yellow-200 text-lg">Demo Mode</h4>
                  <p className="text-sm text-yellow-300/90 mt-2">
                    System configuration features are not yet implemented. Toggles and logs shown below are for demonstration purposes only.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Server Status - Enhanced Card with Real Data */}
              <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-blue-500/30 shadow-xl">
                <h3 className="text-lg font-semibold mb-4 flex items-center bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  <span className="mr-2 text-2xl">🖥️</span>
                  Server Status
                </h3>
                {health ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">Backend:</span>
                      <span className={`font-semibold flex items-center gap-1 ${
                        health.status === 'healthy' ? 'text-green-400' : 'text-yellow-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full animate-pulse ${
                          health.status === 'healthy' ? 'bg-green-400' : 'bg-yellow-400'
                        }`}></span>
                        {health.status === 'healthy' ? 'Running' : 'Degraded'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">Database:</span>
                      <span className={`font-semibold flex items-center gap-1 ${
                        health.database.connected ? 'text-green-400' : 'text-red-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          health.database.connected ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                        }`}></span>
                        {health.database.connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    {health.redis && (
                      <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                        <span className="text-gray-300 font-medium">Redis:</span>
                        <span className={`font-semibold flex items-center gap-1 ${
                          health.redis.connected ? 'text-green-400' : 'text-red-400'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${
                            health.redis.connected ? 'bg-green-400 animate-pulse' : 'bg-red-400'
                          }`}></span>
                          {health.redis.connected ? 'Connected' : 'Disconnected'}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">Uptime:</span>
                      <span className="text-blue-400 font-semibold">
                        {Math.floor(health.uptime / 3600)}h {Math.floor((health.uptime % 3600) / 60)}m
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-4">
                    Loading server status...
                  </div>
                )}
              </div>

              {/* System Settings - Enhanced Card with Real Toggles */}
              <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-purple-500/30 shadow-xl">
                <h3 className="text-lg font-semibold mb-4 flex items-center bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
                  <span className="mr-2 text-2xl">⚙️</span>
                  System Settings
                </h3>
                {systemSettings ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">Maintenance Mode</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={systemSettings.maintenanceMode}
                          onChange={(e) => handleSettingToggle('maintenanceMode', e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">New User Registration</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={systemSettings.allowRegistration}
                          onChange={(e) => handleSettingToggle('allowRegistration', e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                      <span className="text-gray-300 font-medium">AI Players</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          checked={systemSettings.aiPlayersEnabled}
                          onChange={(e) => handleSettingToggle('aiPlayersEnabled', e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-4">
                    Loading settings...
                  </div>
                )}
              </div>
            </div>

            {/* Logs - Enhanced Glass Card with Real Data */}
            <div className="bg-gray-800/70 backdrop-filter backdrop-blur-lg p-6 rounded-xl border border-gray-700/50 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center bg-gradient-to-r from-green-400 to-emerald-600 bg-clip-text text-transparent">
                  <span className="mr-2 text-2xl">📋</span>
                  Recent System Logs
                </h3>
                <button 
                  onClick={loadSystemLogs}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  🔄 Refresh
                </button>
              </div>
              <div className="bg-black/80 p-4 rounded-lg font-mono text-sm space-y-1 max-h-60 overflow-y-auto border border-green-500/20">
                {systemLogs.length > 0 ? (
                  systemLogs.map((log, index) => (
                    <div 
                      key={index} 
                      className={`${
                        log.level === 'info' ? 'text-green-400' :
                        log.level === 'warn' ? 'text-yellow-400' :
                        log.level === 'error' ? 'text-red-400' :
                        'text-blue-400'
                      }`}
                    >
                      [{log.level.toUpperCase()}] {log.message}
                    </div>
                  ))
                ) : (
                  <>
                    <div className="text-green-400">[INFO] Server started on port 3001</div>
                    <div className="text-blue-400">[INFO] Database connected successfully</div>
                    <div className="text-green-400">[INFO] User {user?.username} logged in</div>
                    <div className="text-yellow-400">[WARN] High memory usage detected</div>
                    <div className="text-green-400">[INFO] Game room created by cardmaster</div>
                    <div className="text-blue-400">[INFO] WebSocket connection established</div>
                    <div className="text-green-400">[INFO] Game completed in room-1</div>
                    <div className="text-gray-500">[DEBUG] Cache hit for user stats</div>
                    <div className="text-green-400">[INFO] Admin panel accessed by {user?.username}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Announcement Dialog */}
      {announcementDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-2xl">
            <h3 className="text-xl font-semibold text-white mb-4">Send Announcement</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={announcementDialog.title}
                  onChange={(e) => setAnnouncementDialog({...announcementDialog, title: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Announcement Title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={announcementDialog.message}
                  onChange={(e) => setAnnouncementDialog({...announcementDialog, message: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                  placeholder="Announcement message to all users..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={announcementDialog.type}
                  onChange={(e) => setAnnouncementDialog({...announcementDialog, type: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setAnnouncementDialog({ ...announcementDialog, isOpen: false })}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                disabled={announcementDialog.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSendAnnouncement}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50"
                disabled={announcementDialog.isLoading}
              >
                {announcementDialog.isLoading ? 'Sending...' : 'Send Announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      {createUserDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-2xl">
            <h3 className="text-xl font-semibold text-white mb-4">Create New User</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={createUserDialog.username}
                  onChange={(e) => setCreateUserDialog({...createUserDialog, username: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="johndoe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={createUserDialog.email}
                  onChange={(e) => setCreateUserDialog({...createUserDialog, email: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password <span className="text-red-400">*</span>
                </label>
                <input
                  type="password"
                  value={createUserDialog.password}
                  onChange={(e) => setCreateUserDialog({...createUserDialog, password: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={createUserDialog.displayName}
                  onChange={(e) => setCreateUserDialog({...createUserDialog, displayName: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role
                </label>
                <select
                  value={createUserDialog.role}
                  onChange={(e) => setCreateUserDialog({...createUserDialog, role: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setCreateUserDialog({
                  isOpen: false,
                  username: '',
                  email: '',
                  password: '',
                  displayName: '',
                  role: 'user',
                  isLoading: false
                })}
                disabled={createUserDialog.isLoading}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={createUserDialog.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createUserDialog.isLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.type === 'danger' ? 'Yes, Delete' : 'Confirm'}
        isLoading={confirmDialog.isLoading}
      />
    </div>
  );
};

export default AdminPanelNew;
